/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { RadioGroup, FormControlLabel, Radio, TextField, Box, InputAdornment, CircularProgress } from '@mui/material';

import { isEmpty } from 'lodash';
import { CreateUserFields, Paths, UserTypes, BooleanConstants, Role } from '../../../../../Shared/Constants/App';
import { TeamAssignmentActionType } from '../../../../../API';
import { Patterns } from '../../../../../Shared/Constants';
import AllJobsApiService from '../../../../Jobs/Services/JobManagementService';
import { getStoredCustomerDetails } from '../../../../../Shared/Utilities/utils';
import { OrganizationService } from '../../../../../Shared/Services/OrganizationService';
import PageTitle from '../../../../../Shared/Components/Common/PageTitle/PageTitle';
import Button, { ButtonType } from '../../../../../Shared/Components/Common/Buttons/Button';
import { CreateUserOptions } from '../../AllSchedules/AllSchedules.Model';
import SnackbarMessage from '../../../../../Shared/Components/Common/SnackbarMessage/SnackbarMessage';

import { Icons } from '../../../../../Shared/Constants/Icons';
import theme from '../../../../../Shared/Themes/theme';

import { CustomerContainer } from '../../../../../Shared/Components/Layout/styles';
import { wrapperStyles } from '../../AllSchedules/AllJobs.Style';

// eslint-disable-next-line sonarjs/cognitive-complexity
const CreateUser: React.FC = () => {
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [isEmailValid, setIsEmailValid] = useState<boolean>(true);
  const [isUserNameValid, setIsUserNameValid] = useState<boolean>(true);
  const [roleId, setRoleId] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [userName, setUserName] = useState<string>('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isLoading, setIsLoading] = useState(false);
  const [isGeneratingUsername, setIsGeneratingUsername] = useState(false);

  const { t } = useTranslation(['jobManagement']);
  const { textField, radioButton, radioButtonText, manageUserIcons } = wrapperStyles;
  const location = useLocation();
  const type = location?.state?.type;
  const navigate = useNavigate();

  const vendorId = location?.state?.selectedRowsData?.[0]?.vendorId;

  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    setSelectedOption(newValue);
    setIsEmailValid(true);
    if (newValue === CreateUserOptions.Generate.toString()) {
      createUserWithVendorNumber();
    } else {
      setUserName('');
      setName('');
      setEmail('');
      setPassword('');
    }
  };

  const getRoles = useCallback(async () => {
    try {
      const organizationRolesResponse = await OrganizationService.getOrganizationRoles(vendorId);
      const associateRole = organizationRolesResponse?.data[0]?.roles.filter(
        (role: { roleName: string }) => (role.roleName as Role) === Role.Associate
      );
      const supervisorRole = organizationRolesResponse?.data[0]?.roles.filter(
        (role: { roleName: string }) => (role.roleName as Role) === Role.Supervisor
      );
      const userRoleId = type === UserTypes.Associates ? associateRole : supervisorRole;
      setRoleId(userRoleId?.[0]?.roleId);
    } catch (e) {
      console.log(e);
    }
  }, []);

  const createUserWithVendorNumber = async () => {
    const currentOption = selectedOption;
    try {
      setIsGeneratingUsername(true);
      const { selectedRowsData } = location.state;
      const vendorNumber = selectedRowsData[0]?.vendorNumber;
      const response = await AllJobsApiService.generateGenericUserName(vendorNumber);
      const generatedUsername = response.data[0];
      if (currentOption === CreateUserOptions.Generate.toString()) {
        setUserName(generatedUsername);
        setName(generatedUsername);
        setEmail(`${generatedUsername}@${CreateUserFields.Email}`);
        setPassword(CreateUserFields.Password);
      }
    } catch (error) {
      console.error(t('vendor:errorOccurred'), error);
    } finally {
      setIsGeneratingUsername(false);
    }
  };

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const handleCreateUser = async () => {
    try {
      setIsLoading(true);
      const customerDetails = JSON.parse(localStorage.getItem('customerDetails') || '{}');
      const managerId = customerDetails?.userId ?? '';
      const { selectedRowsData } = location.state;
      const userInput = {
        username: name ?? '',
        email: email ?? '',
        roleId: roleId ?? '',
        organizationId: location?.state?.selectedRowsData[0]?.vendorId,
        managerId: managerId,
      };
      const createUserResponse = await OrganizationService.createUser(userInput);
      if (!isEmpty(createUserResponse?.data?.errorCode)) {
        setSnackbarMessage(createUserResponse?.data?.errorCode);
        setSnackbarOpen(true);
        setSnackbarSeverity('error');
        return true;
      } else if (!isEmpty(createUserResponse.errors)) {
        setSnackbarMessage(t('vendor:errorOccurred'));
        setSnackbarOpen(true);
        setSnackbarSeverity('error');
        return;
      }
      const newUserId = createUserResponse?.data?.userId;
      const newCognitoUserId = createUserResponse?.data?.cognitoUserId;
      if (!newUserId) {
        throw new Error('User creation failed - no userId returned');
      }
      if (selectedOption !== CreateUserOptions.Invite.toString()) {
        const passwordToSet =
          selectedOption === CreateUserOptions.Generate.toString() ? CreateUserFields.Password : password;
        const isPermanentPassword = BooleanConstants.True;
        const setPasswordResponse = await AllJobsApiService.setUserPassword(
          newCognitoUserId,
          passwordToSet,
          isPermanentPassword
        );

        if (!setPasswordResponse.success) {
          throw new Error(setPasswordResponse.errors[0]?.message || 'Password setting failed');
        }
        console.log('Password set successfully');
      }
      const payload = selectedRowsData.map((row: any) => ({
        workOrderId: row.workOrderId,
        workOrderScheduleMasterId: row.woScheduleMasterId,
        userId: newUserId,
        organizationId: row.vendorId,
        roleId: roleId,
        isSupervisor: type === UserTypes.Supervisors,
        startDate: row.scheduleStartDate || null,
        endDate: row.scheduleEndDate || null,
      }));
      const actionType = TeamAssignmentActionType.Add;
      const result = await AllJobsApiService.manageScheduleAssignments(payload, actionType);
      if (
        (result.errors && result.errors.length > 0) ||
        (result.data?.manageScheduleAssignmentsFailure &&
          result.data.manageScheduleAssignmentsFailure.length > 0 &&
          result.data.manageScheduleAssignmentsFailure.some(
            (failure: { errorMessage: string }) => failure.errorMessage !== 'ASSIGNMENT_ALREADY_EXISTS'
          )) ||
        result.data?.manageScheduleAssignmentsSuccess === null
      ) {
        const errorMessages =
          result.errors?.map((error: { message: any }) => error.message).join(', ') || t('messages:errorOccurred');
        setSnackbarMessage(errorMessages);
        setSnackbarOpen(true);
        setSnackbarSeverity('error');
        setIsLoading(true);
      } else {
        const redirectPath = Paths.ManageUser;
        const successMessage =
          type === UserTypes.Supervisors
            ? t('messages:successfullyCreatedAndAssignedSupervisor')
            : t('messages:successfullyCreatedAndAssignedAssociate');
        setSnackbarMessage(successMessage);
        setSnackbarOpen(true);
        setSnackbarSeverity('success');
        setTimeout(() => {
          navigate(redirectPath);
        }, 1000);
      }
      setIsLoading(false);
    } catch (error) {
      setSnackbarMessage(t('vendor:errorOccurred'));
      setSnackbarOpen(true);
      setSnackbarSeverity('error');
    } finally {
      setIsLoading(false);
    }
  };

  const validateEmail = (email: string) => {
    if (selectedOption === CreateUserOptions.Generate.toString()) {
      setIsEmailValid(true);
      return true;
    }
    const isValid = Patterns.Email.test(email);
    setIsEmailValid(isValid);
    return isValid;
  };

  const validateUserName = (userName: string) => {
    if (selectedOption === CreateUserOptions.Generate.toString()) {
      setIsUserNameValid(true);
      return true;
    }
    const isValid = Patterns.AlphaNumericWithSpaces.test(userName);
    setIsUserNameValid(isValid);
    return isValid;
  };

  const isButtonDisabled = () => {
    if (selectedOption === CreateUserOptions.Invite.toString()) {
      return !name || !email || !isEmailValid || !isUserNameValid;
    } else if (
      selectedOption === CreateUserOptions.SetPassword.toString() ||
      selectedOption === CreateUserOptions.Generate.toString()
    ) {
      const emailToCheck =
        selectedOption === CreateUserOptions.Generate.toString() ? `${userName}@${CreateUserFields.Email}` : email;
      return !name || !emailToCheck || !password || !isEmailValid || !isUserNameValid;
    }
    return true;
  };

  useEffect(() => {
    getRoles();
  }, []);

  useEffect(() => {
    if (selectedOption === CreateUserOptions.Generate.toString()) {
      createUserWithVendorNumber();
    }
  }, [selectedOption]);
  return (
    <>
      <PageTitle title={t('jobManagement:createUser')} />
      <CustomerContainer>
        <RadioGroup value={selectedOption} onChange={handleOptionChange} sx={{ gap: '2rem', margin: '3rem 0' }}>
          {type === UserTypes.Associates && location?.state?.selectedRowsData[0]?.vendorNumber !== null && (
            <FormControlLabel
              value="generate"
              control={
                <Radio
                  sx={{
                    ...radioButton,
                  }}
                />
              }
              label={t('jobManagement:generateGenericUserName')}
              sx={{
                ...radioButtonText,
              }}
            />
          )}
          <FormControlLabel
            value="setPassword"
            control={
              <Radio
                sx={{
                  ...radioButton,
                }}
                disabled={isGeneratingUsername}
              />
            }
            label={t('jobManagement:setAPassword')}
            sx={{
              ...radioButtonText,
            }}
          />
          <FormControlLabel
            value="invite"
            control={
              <Radio
                sx={{
                  ...radioButton,
                }}
                disabled={isGeneratingUsername}
              />
            }
            label={t('jobManagement:inviteWithTemporaryPassword')}
            sx={{
              ...radioButtonText,
            }}
          />
        </RadioGroup>
        {selectedOption && (
          <>
            <Box
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '1rem',
              }}>
              <TextField
                label={
                  <span>
                    {t('addUsers:name')}
                    <span className="mandatory-field">*</span>
                  </span>
                }
                value={selectedOption === CreateUserOptions.Generate.toString() ? userName : name}
                error={selectedOption !== CreateUserOptions.Generate.toString() && !isUserNameValid}
                helperText={
                  selectedOption !== CreateUserOptions.Generate.toString() && !isUserNameValid
                    ? t('jobManagement:EnterValidUserName')
                    : ''
                }
                onChange={(e) => {
                  if (selectedOption !== CreateUserOptions.Generate.toString()) {
                    setName(e.target.value);
                    validateUserName(e.target.value);
                  }
                }}
                sx={{ ...textField, marginBottom: '2rem' }}
                disabled={selectedOption === CreateUserOptions.Generate.toString() || isGeneratingUsername}
                InputProps={{
                  endAdornment:
                    isGeneratingUsername && !userName ? (
                      <InputAdornment position="end">
                        <CircularProgress size={24} sx={{ color: theme.palette.primary.dark }} />
                      </InputAdornment>
                    ) : null,
                }}
              />
              <TextField
                label={
                  <span>
                    {t('signIn:emailAddress')}
                    <span className="mandatory-field">*</span>
                  </span>
                }
                value={
                  selectedOption === CreateUserOptions.Generate.toString()
                    ? userName
                      ? `${userName}@${CreateUserFields.Email}`
                      : ''
                    : email
                }
                onChange={(e) => {
                  if (selectedOption !== CreateUserOptions.Generate.toString()) {
                    setEmail(e.target.value);
                    validateEmail(e.target.value);
                  }
                }}
                error={selectedOption !== CreateUserOptions.Generate.toString() && !isEmailValid}
                helperText={
                  selectedOption !== CreateUserOptions.Generate.toString() && !isEmailValid
                    ? t('jobManagement:EnterValidEmailAddress')
                    : ''
                }
                sx={{ ...textField, marginBottom: '2rem' }}
                disabled={selectedOption === CreateUserOptions.Generate.toString() || isGeneratingUsername}
                InputProps={{
                  endAdornment:
                    isGeneratingUsername && !userName ? (
                      <InputAdornment position="end">
                        <CircularProgress size={24} sx={{ color: theme.palette.primary.dark }} />
                      </InputAdornment>
                    ) : null,
                }}
              />
              {(selectedOption === CreateUserOptions.SetPassword.toString() ||
                selectedOption === CreateUserOptions.Generate.toString()) && (
                <TextField
                  label={
                    <span>
                      {t('signIn:passwordLabel')}
                      <span className="mandatory-field">*</span>
                    </span>
                  }
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    if (selectedOption !== CreateUserOptions.Generate.toString()) {
                      setPassword(e.target.value);
                    }
                  }}
                  sx={{ ...textField, marginBottom: '2rem' }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        {showPassword ? (
                          <img
                            src={Icons.VisibilityOnIcon}
                            onClick={() => setShowPassword(!showPassword)}
                            style={{ cursor: 'pointer' }}
                          />
                        ) : (
                          <img
                            src={Icons.VisibilityOffIcon}
                            onClick={() => setShowPassword(!showPassword)}
                            style={{ cursor: 'pointer' }}
                          />
                        )}
                      </InputAdornment>
                    ),
                  }}
                  disabled={selectedOption === CreateUserOptions.Generate.toString()}
                />
              )}
            </Box>
            <Box sx={{ ...manageUserIcons, marginTop: '2rem' }}>
              <Button
                type={ButtonType.Primary}
                label={t('jobManagement:createUser')}
                onClick={handleCreateUser}
                disabled={isButtonDisabled()}
                isLoading={isLoading}
              />
            </Box>
            <SnackbarMessage
              open={snackbarOpen}
              successMessage={snackbarMessage}
              errorMessage={snackbarMessage}
              severity={snackbarSeverity}
              onClose={() => setSnackbarOpen(false)}
            />
          </>
        )}
      </CustomerContainer>
    </>
  );
};

export default CreateUser;
